<template>
  <div>
    <div class="px-2">
      <div
        ref="initialInputHolder"
        class="border border-gray-300 dark:border-gray-700 focus-within:border-white dark:focus-within:border-blue-500 bg-white/50 dark:bg-[#0d1a20]/50 backdrop-blur-md rounded-xl relative transition-colors shadow-sm focus-within:ring focus-within:ring-australianMint dark:focus-within:ring-blue-500/50"
        :class="{ 'mb-5': showGenericExamples }"
        @focusin="handleFocusIn"
        @focusout="handleFocusOut"
      >
        <!-- Loading Overlay -->
        <div
          v-if="isLoading"
          class="absolute inset-0 z-50 rounded-xl bg-white/80 dark:bg-[#0d1a20]/80 backdrop-blur-sm flex flex-col items-center justify-center transition-all duration-300"
        >
          <div
            class="flex items-center gap-3 text-blue-600 dark:text-blue-400 font-bold tracking-wide text-sm"
          >
            <i class="fas fa-circle-notch fa-spin text-lg"></i>
            <span>{{ loadingMessage || "Processing..." }}</span>
          </div>
        </div>

        <div>
          <textarea
            ref="initialInput"
            v-model="initialInput"
            class="rounded-xl bg-transparent text-gray-900 dark:text-gray-100 border-0 outline-none focus:outline-none focus:ring-0 w-full resize-none block placeholder-gray-500 dark:placeholder-gray-400 py-2.5 px-4 leading-tight"
            rows="1"
            :placeholder="placeholder"
            @input="handleInitialInput"
            @keydown.down="handleArrowDown"
            @keydown.up="handleArrowUp"
            @keydown.enter="handleEnter"
          />
        </div>
        <div
          v-show="isFocused || initialInput.length > 0"
          class="w-full transition-all duration-300 overflow-hidden"
        >
          <div
            v-if="loadingFollowupQuestions"
            class="px-4 pt-1 pb-2 text-gray-500 dark:text-gray-400 italic text-xs animate-pulse flex items-center"
          >
            <i class="fas fa-circle-notch fa-spin mr-2 text-blue-500"></i>
            Fetching follow-up suggestions...
          </div>
          <div
            v-else-if="followupQuestions?.length && initialInput.length === 0"
            ref="followupQuestion"
            class="px-4 pb-2 pt-1 flex flex-col gap-2 items-start"
          >
            <span
              class="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500"
            >
              Suggestions
            </span>
            <button
              v-for="(question, index) in followupQuestions.slice(0, 2)"
              :key="index"
              class="bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200/50 dark:border-blue-800/30 text-blue-800 dark:text-blue-300 px-1.5 py-2 rounded-lg text-xs font-semibold transition-colors shadow-sm text-left max-w-full flex items-start w-full"
              @mousedown.prevent="selectSuggestion(question)"
            >
              <i
                class="fa-solid fa-sparkles text-blue-500/70 mr-2 mt-0.5 shrink-0"
              ></i>
              <span class="leading-snug">{{ question }}</span>
            </button>
          </div>
          <div
            ref="controls"
            class="w-full flex flex-wrap justify-between px-2 pt-2"
          >
            <div
              class="flex w-full md:w-auto my-2 items-center rounded-lg shadow-sm border border-gray-300 dark:border-gray-700 overflow-hidden bg-gray-100/50 dark:bg-[#12242b]/50 divide-x divide-gray-300 dark:divide-gray-700"
            >
              <context-popper
                :not-allowed="true"
                :placement="'top'"
                text="Papers are always enabled."
                class="flex-1 sm:flex-none"
              >
                <button
                  class="px-4 py-1.5 w-full flex justify-center items-center text-sm font-bold transition-all duration-200"
                  :class="{
                    'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300':
                      modes.papers,
                    'text-gray-600 dark:text-gray-400 hover:bg-gray-200/50 dark:hover:bg-[#1a343d]/50 hover:text-gray-800 dark:hover:text-gray-200':
                      !modes.papers,
                  }"
                >
                  <i
                    class="fa-solid fa-file-lines mr-1.5"
                    :class="
                      modes.papers
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-400 dark:text-gray-500'
                    "
                  ></i>
                  Papers
                </button>
              </context-popper>
              <button
                class="px-4 py-1.5 flex-1 sm:flex-none flex justify-center items-center text-sm font-bold transition-all duration-200"
                :class="{
                  'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300':
                    modes.experts,
                  'text-gray-600 dark:text-gray-400 hover:bg-gray-200/50 dark:hover:bg-[#1a343d]/50 hover:text-gray-800 dark:hover:text-gray-200':
                    !modes.experts,
                }"
                @click="toggleMode('experts')"
              >
                <i
                  class="fa-solid fa-user-graduate mr-1.5"
                  :class="
                    modes.experts
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-gray-400 dark:text-gray-500'
                  "
                ></i>
                Experts
              </button>
              <template
                v-if="
                  collaborationSpaceMetadata?.isResearchCompassPatentEnabled
                "
              >
                <button
                  class="px-4 py-1.5 flex-1 sm:flex-none flex justify-center items-center text-sm font-bold transition-all duration-200"
                  :class="{
                    'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300':
                      modes.patents,
                    'text-gray-600 dark:text-gray-400 hover:bg-gray-200/50 dark:hover:bg-[#1a343d]/50 hover:text-gray-800 dark:hover:text-gray-200':
                      !modes.patents,
                  }"
                  @click="toggleMode('patents')"
                >
                  <i
                    class="fa-solid fa-lightbulb mr-1.5"
                    :class="
                      modes.patents
                        ? 'text-purple-600 dark:text-purple-400'
                        : 'text-gray-400 dark:text-gray-500'
                    "
                  ></i>
                  Patents
                </button>
              </template>
              <template
                v-if="
                  collaborationSpaceMetadata?.isResearchCompassIndustryPartnersEnabled
                "
              >
                <button
                  class="px-4 py-1.5 flex-1 sm:flex-none flex justify-center items-center text-sm font-bold transition-all duration-200"
                  :class="{
                    'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300':
                      modes.industryPartners,
                    'text-gray-600 dark:text-gray-400 hover:bg-gray-200/50 dark:hover:bg-[#1a343d]/50 hover:text-gray-800 dark:hover:text-gray-200':
                      !modes.industryPartners,
                  }"
                  @click="toggleMode('industryPartners')"
                >
                  <i
                    class="fa-solid fa-building mr-1.5"
                    :class="
                      modes.industryPartners
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-gray-400 dark:text-gray-500'
                    "
                  ></i>
                  Industry Partners
                </button>
              </template>
            </div>
            <div class="flex w-full md:w-auto justify-end my-2">
              <button
                class="bg-gray-100/80 dark:bg-[#12242b]/80 border-y border-l border-gray-300 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-[#1a343d] text-gray-800 dark:text-gray-200 px-4 py-1.5 rounded-l-lg relative transition-colors text-sm font-bold shadow-sm"
                @click="toggleFiltersModal()"
              >
                <span
                  class="-mt-1 right-1 text-[10px] bg-blue-600 dark:bg-blue-500 rounded-full text-white flex items-center justify-center w-[16px] h-[16px] absolute font-mono"
                  >{{ filterCount }}</span
                >
                <i class="fa-solid fa-gear mr-1.5"></i>
                Settings & Filters
              </button>
              <button
                class="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 border border-blue-600 dark:border-blue-500 transition-colors text-white px-6 py-1.5 rounded-r-lg text-sm font-bold shadow-sm"
                @click="evaluateQuestionBreadth()"
              >
                <span v-if="isLoading"
                  ><i class="fas fa-circle-notch fa-spin"></i
                ></span>
                <span v-else>Ask</span>
              </button>
            </div>
          </div>
        </div>
        <div
          v-show="areSuggestionsVisible && !isLoading"
          ref="suggestions"
          v-on-click-outside="hideSuggestions"
          class="absolute w-full z-50 bg-white/95 dark:bg-[#0d1a20]/95 backdrop-blur-xl border border-gray-300 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-all duration-300"
          :style="{ top: `${autocompleteHeightOffset}px` }"
        >
          <div
            v-if="autocompleteIsLoading"
            class="py-4 text-center w-full text-gray-500 dark:text-gray-400"
          >
            <i class="fas fa-circle-notch fa-spin"></i>
          </div>
          <div v-else class="flex flex-col py-1">
            <div
              v-for="(suggestion, index) in suggestions"
              :key="suggestion"
              class="py-2.5 px-4 cursor-pointer text-sm transition-colors flex items-start gap-2"
              :class="{
                'bg-blue-600 text-white': index === selectedSuggestionIndex,
                'text-gray-800 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20':
                  index !== selectedSuggestionIndex,
              }"
              @mousemove="selectedSuggestionIndex = index"
              @mousedown.prevent="selectSuggestion(suggestion)"
            >
              <i
                class="fa-solid fa-sparkles mt-0.5 shrink-0"
                :class="
                  index === selectedSuggestionIndex
                    ? 'text-white/70'
                    : 'text-blue-500/70'
                "
              ></i>
              <span class="leading-snug">{{ suggestion }}</span>
            </div>
          </div>
        </div>
        <div
          v-show="isFeedbackVisible"
          ref="feedback"
          class="absolute w-full lg:w-8/12 z-20 mt-2 transition-all duration-300"
          :style="{ top: `${feedbackHeightOffset}px` }"
        >
          <div
            class="rounded-2xl px-5 py-4 backdrop-blur-xl shadow-2xl border"
            :class="{
              'bg-red-50/80 dark:bg-red-900/30 border-red-500/50 text-red-900 dark:text-red-100':
                feedback.score === 1,
              'bg-yellow-50/80 dark:bg-yellow-900/30 border-yellow-500/50 text-yellow-900 dark:text-yellow-100':
                feedback.score === 2,
              'bg-green-50/80 dark:bg-green-900/30 border-green-500/50 text-green-900 dark:text-green-100':
                feedback.score >= 3,
            }"
          >
            <div v-if="feedbackIsLoading" class="py-4 text-center w-full">
              <i class="fas fa-circle-notch fa-spin text-2xl opacity-70"></i>
            </div>
            <div v-else>
              <div class="w-full flex justify-end mb-1">
                <i
                  class="cursor-pointer text-xl fa-solid fa-xmark opacity-70 hover:opacity-100 transition-opacity"
                  @click="hideFeedback()"
                ></i>
              </div>
              <p
                class="text-sm font-medium leading-relaxed"
                v-html="feedback.message"
              />
              <div
                v-if="initialInput?.length > 0"
                class="w-full flex justify-end mt-4 gap-3"
              >
                <button
                  class="bg-white/50 dark:bg-black/20 hover:bg-white/80 dark:hover:bg-black/40 text-gray-800 dark:text-gray-200 border border-gray-300/50 dark:border-gray-600/50 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-xl shadow-sm transition-all duration-300 flex items-center justify-center min-w-[120px]"
                  @click="createResearchCompassQuestion()"
                >
                  <span v-if="isLoading"
                    ><i class="fas fa-circle-notch fa-spin"></i
                  ></span>
                  <span v-else>Ask anyway</span>
                </button>
                <button
                  class="bg-blueDianne hover:bg-blueDianne/90 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-xl shadow-md transition-all duration-300 flex items-center justify-center min-w-[120px]"
                  @click="hideFeedback()"
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showGenericExamples">
        <p class="text-sm mt-3">Need inspiration? Here are some examples:</p>
        <ul class="list-disc text-sm ml-5 mt-2">
          <li
            v-for="example in examples"
            :key="example"
            class="underline cursor-pointer"
            @mousedown.prevent="selectSuggestion(example)"
          >
            {{ example }}
          </li>
        </ul>
      </div>
    </div>
    <right-side-modal ref="filtersModal" :showing="false" :hide-footer="true">
      <template #header>
        <h4 class="text-xl font-headline">Settings & Filters</h4>
      </template>
      <template #body>
        <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <label
            class="font-bold text-gray-900 dark:text-white uppercase tracking-wider text-xs block mb-4"
            >Settings</label
          >
          <div class="mb-6">
            <span
              class="text-gray-500 dark:text-gray-400 font-bold block mb-2 text-sm"
            >
              Generative Model
            </span>
            <div class="flex flex-col gap-2">
              <label
                v-for="modelOption in models"
                :key="modelOption.shortLabel"
                class="flex items-center text-sm transition-opacity"
                :class="{
                  'opacity-40 cursor-not-allowed select-none text-gray-400 dark:text-gray-500': modelOption.disabled,
                  'text-gray-700 dark:text-gray-300 cursor-pointer': !modelOption.disabled
                }"
              >
                <input
                  type="radio"
                  name="generativeModel"
                  :value="modelOption.value"
                  :checked="selectedModel === modelOption.value"
                  :disabled="modelOption.disabled"
                  class="mr-2 text-blue-600 focus:ring-blue-500 bg-white dark:bg-[#12242b] border-gray-300 dark:border-gray-700 disabled:opacity-50"
                  @change="!modelOption.disabled && updateModel(modelOption.value)"
                />
                <span class="flex items-center gap-2">
                  {{ modelOption.label }}
                  <span
                    v-if="modelOption.disabled"
                    class="px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider rounded bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/30"
                  >
                    Suspended
                  </span>
                </span>
              </label>
            </div>

            <transition
              mode="out-in"
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="opacity-0 -translate-y-2"
              leave-active-class="transition duration-200 ease-in"
              leave-to-class="opacity-0 -translate-y-2"
            >
              <!-- Claude Sonnet 4.6 Notice (Green) -->
              <div
                v-if="isAnthropicSonnet"
                class="mt-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-xs space-y-3 transition-all duration-300"
              >
                <div class="flex items-start">
                  <span class="mr-2 text-md mt-0.5 select-none">🛡️</span>
                  <div>
                    <h5
                      class="font-bold text-emerald-900 dark:text-emerald-200 text-sm mb-1"
                    >
                      Vertex AI & Anthropic Enterprise Data Policy
                    </h5>
                    <p class="leading-relaxed mb-1">
                      Google Cloud and Anthropic contractually guarantee that your research prompts and model responses
                      are <strong>never</strong> used to train Google, Anthropic, or third-party foundation models.
                    </p>
                    <p class="leading-relaxed mb-1">
                      Your data remains strictly isolated within Google Cloud's secure enterprise infrastructure boundaries.
                      Google acts as the sole data processor, and your interactions are <strong>never</strong> transmitted to Anthropic for model training or data logging.
                    </p>
                    <p class="leading-relaxed">
                      For compliance and safety, Google performs automated abuse monitoring under Google Cloud Terms, with security logs temporarily retained for up to 30 days before permanent deletion.
                      Read the official
                      <a
                        href="https://cloud.google.com/terms/service-terms#1"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >Vertex AI Terms</a
                      >,
                      <a
                        href="https://cloud.google.com/terms/data-processing-addendum"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >DPA</a
                      >, and
                      <a
                        href="https://www.anthropic.com/trust"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >Anthropic Trust Commitments</a
                      >.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Claude 5 Fable Notice (Yellow) -->
              <div
                v-else-if="isAnthropicFable"
                class="mt-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 text-amber-800 dark:text-amber-300 text-xs space-y-3 transition-all duration-300"
              >
                <!-- Suspended Notice State -->
                <div v-if="isFableSuspended" class="flex items-start">
                  <span class="mr-2 text-md mt-0.5 select-none">⚠️</span>
                  <div>
                    <h5
                      class="font-bold text-amber-900 dark:text-amber-200 text-sm mb-1"
                    >
                      Model Access Suspended
                    </h5>
                    <p class="leading-relaxed mb-1">
                      Claude 5 Fable is currently unavailable. This model has been suspended in accordance with the latest US federal export controls and AI safety regulatory enforcements.
                    </p>
                    <p class="leading-relaxed font-semibold text-amber-700 dark:text-amber-400">
                      Please select an alternate model (e.g., Claude 4.6 Sonnet or Gemini 3.1 Pro) to run your queries.
                    </p>
                  </div>
                </div>

                <!-- Active Policy Notice State -->
                <template v-else>
                  <div class="flex items-start">
                    <span class="mr-2 text-md mt-0.5 select-none">🛡️</span>
                    <div>
                      <h5
                        class="font-bold text-amber-900 dark:text-amber-200 text-sm mb-1"
                      >
                        Vertex AI & Anthropic Covered Model Policy
                      </h5>
                      <p class="leading-relaxed mb-1">
                        Google Cloud and Anthropic contractually guarantee that your research prompts and model responses
                        are <strong>never</strong> used to train Google, Anthropic, or third-party foundation models.
                      </p>
                      <p class="leading-relaxed mb-1">
                        Because Claude 5 Fable is an Anthropic Covered Model, it requires a mandatory 30-day minimum safety
                        retention period for prompts and completions to support safety classifier operations. Standard Zero Data
                        Retention (ZDR) is disabled for this model.
                      </p>
                      <p class="leading-relaxed mb-1">
                        To maintain compliance with strict safety boundaries, your query metadata and flagged prompt telemetry
                        may be shared with Anthropic's safety monitoring systems solely for automated abuse reviews and safety investigations.
                      </p>
                      <p class="leading-relaxed">
                        Your usage is governed by Google Cloud terms and Anthropic's licensing requirements. Read the official
                        <a
                          href="https://cloud.google.com/terms/service-terms#1"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="underline font-semibold hover:text-amber-600 transition-colors"
                          >Vertex AI Terms</a
                        >,
                        <a
                          href="https://cloud.google.com/terms/data-processing-addendum"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="underline font-semibold hover:text-amber-600 transition-colors"
                          >DPA</a
                        >, and the
                        <a
                          href="https://www.anthropic.com/trust"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="underline font-semibold hover:text-amber-600 transition-colors"
                          >Anthropic Trust Commitments</a
                        >.
                      </p>
                    </div>
                  </div>

                  <!-- Fable Specific Warnings -->
                  <div
                    class="pl-6 pt-2 border-t border-amber-200/40 dark:border-amber-900/20 flex items-start"
                  >
                    <span class="mr-2 text-xs select-none">⚠️</span>
                    <p class="leading-relaxed">
                      <strong>Claude 5 Fable restrictions:</strong> Heavily
                      restricted on sensitive topics such as biosecurity, CBRN
                      safety, cyber-operations, and violence.
                    </p>
                  </div>
                </template>
              </div>

              <!-- Google Gemini Models Notice -->
              <div
                v-else-if="isGoogleModel"
                class="mt-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-xs space-y-3 transition-all duration-300"
              >
                <div class="flex items-start">
                  <span class="mr-2 text-md mt-0.5 select-none">🛡️</span>
                  <div>
                    <h5
                      class="font-bold text-emerald-900 dark:text-emerald-200 text-sm mb-1"
                    >
                      Google Vertex AI Enterprise Data Policy
                    </h5>
                    <p class="leading-relaxed mb-1">
                      Google Cloud contractually guarantees that your research prompts and model responses
                      are <strong>never</strong> used to train Google or third-party foundation models.
                    </p>
                    <p class="leading-relaxed mb-1">
                      Your data remains strictly isolated within Google Cloud's secure enterprise infrastructure boundaries.
                    </p>
                    <p class="leading-relaxed">
                      For compliance and safety, Google performs automated abuse monitoring under Google Cloud Terms, with security logs temporarily retained for up to 30 days before permanent deletion.
                      Read the official
                      <a
                        href="https://cloud.google.com/terms/service-terms#1"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >Vertex AI Terms</a
                      > and
                      <a
                        href="https://cloud.google.com/terms/data-processing-addendum"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >DPA</a
                      >.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Azure OpenAI Models Notice -->
              <div
                v-else-if="isAzureModel"
                class="mt-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-xs space-y-3 transition-all duration-300"
              >
                <div class="flex items-start">
                  <span class="mr-2 text-md mt-0.5 select-none">🛡️</span>
                  <div>
                    <h5
                      class="font-bold text-emerald-900 dark:text-emerald-200 text-sm mb-1"
                    >
                      Azure OpenAI Enterprise Data Policy
                    </h5>
                    <p class="leading-relaxed mb-1">
                      Microsoft contractually guarantees that your research prompts and model responses are <strong>never</strong> used to train OpenAI or Microsoft foundation models.
                    </p>
                    <p class="leading-relaxed mb-1">
                      Your data remains isolated within secure Microsoft Azure tenant boundaries, completely separate from other customers and OpenAI.
                    </p>
                    <p class="leading-relaxed">
                      For compliance and security, Microsoft performs automated abuse monitoring and temporarily retains prompts and completions for up to 30 days in secure, encrypted storage.
                      Read the official
                      <a
                        href="https://learn.microsoft.com/en-us/legal/cognitive-services/openai/data-privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >Azure OpenAI Data & Privacy Guide</a
                      >.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Meta LLaMA Models Notice -->
              <div
                v-else-if="isMetaModel"
                class="mt-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-xs space-y-3 transition-all duration-300"
              >
                <div class="flex items-start">
                  <span class="mr-2 text-md mt-0.5 select-none">🛡️</span>
                  <div>
                    <h5
                      class="font-bold text-emerald-900 dark:text-emerald-200 text-sm mb-1"
                    >
                      Vertex AI & Meta LLaMA Enterprise Policy
                    </h5>
                    <p class="leading-relaxed mb-1">
                      Google Cloud contractually guarantees that your research prompts and model responses are <strong>never</strong> used to train Google, Meta, or third-party models.
                    </p>
                    <p class="leading-relaxed mb-1">
                      The model runs within Google Cloud's secure boundaries. No data is ever transmitted to Meta for training, data logging, or retention.
                    </p>
                    <p class="leading-relaxed">
                      Your usage is governed by Google Cloud terms and Meta's licensing requirements. Read the official
                      <a
                        href="https://cloud.google.com/terms/service-terms#1"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >Vertex AI Terms</a
                      >,
                      <a
                        href="https://cloud.google.com/terms/data-processing-addendum"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >DPA</a
                      >, and the
                      <a
                        href="https://llama.meta.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >Meta LLaMA License Agreement</a
                      >.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Mistral AI Models Notice -->
              <div
                v-else-if="isMistralModel"
                class="mt-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-xs space-y-3 transition-all duration-300"
              >
                <div class="flex items-start">
                  <span class="mr-2 text-md mt-0.5 select-none">🛡️</span>
                  <div>
                    <h5
                      class="font-bold text-emerald-900 dark:text-emerald-200 text-sm mb-1"
                    >
                      Vertex AI & Mistral Enterprise Policy
                    </h5>
                    <p class="leading-relaxed mb-1">
                      Google Cloud contractually guarantees that your research prompts and model responses are <strong>never</strong> used to train Google, Mistral, or third-party models.
                    </p>
                    <p class="leading-relaxed mb-1">
                      The model runs within Google Cloud's secure boundaries. No data is ever transmitted to Mistral AI for training, data logging, or retention.
                    </p>
                    <p class="leading-relaxed">
                      Your usage is governed by Google Cloud terms and Mistral AI's licensing terms. Read the official
                      <a
                        href="https://cloud.google.com/terms/service-terms#1"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >Vertex AI Terms</a
                      >,
                      <a
                        href="https://cloud.google.com/terms/data-processing-addendum"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >DPA</a
                      >, and the
                      <a
                        href="https://legal.mistral.ai/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline font-semibold hover:text-emerald-600 transition-colors"
                        >Mistral AI Terms of Service</a
                      >.
                    </p>
                  </div>
                </div>
              </div>
            </transition>
          </div>
          <div>
            <span
              class="text-gray-500 dark:text-gray-400 font-bold block mb-2 text-sm"
            >
              Search Depth
            </span>
            <div class="flex flex-col gap-2">
              <label
                class="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                <input
                  type="radio"
                  name="searchDepth"
                  :value="10"
                  :checked="maxDocCount === 10"
                  class="mr-2 text-blue-600 focus:ring-blue-500 bg-white dark:bg-[#12242b] border-gray-300 dark:border-gray-700"
                  @change="updateMaxDocCount(10)"
                />
                Quick (10 papers)
              </label>
              <label
                class="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                <input
                  type="radio"
                  name="searchDepth"
                  :value="15"
                  :checked="maxDocCount === 15"
                  class="mr-2 text-blue-600 focus:ring-blue-500 bg-white dark:bg-[#12242b] border-gray-300 dark:border-gray-700"
                  @change="updateMaxDocCount(15)"
                />
                Balanced (15 papers)
              </label>
              <label
                class="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                <input
                  type="radio"
                  name="searchDepth"
                  :value="25"
                  :checked="maxDocCount === 25"
                  class="mr-2 text-blue-600 focus:ring-blue-500 bg-white dark:bg-[#12242b] border-gray-300 dark:border-gray-700"
                  @change="updateMaxDocCount(25)"
                />
                Deep (25 papers)
              </label>
            </div>
          </div>
        </div>
        <div class="px-5 py-4 pb-10">
          <label
            class="font-bold text-gray-900 dark:text-white uppercase tracking-wider text-xs block mb-4"
            >Filters</label
          >
          <div class="mb-6">
            <label
              class="flex items-center text-gray-700 dark:text-gray-300 font-bold text-sm cursor-pointer"
              ><input
                v-model="restrictYear"
                type="checkbox"
                class="mr-2 text-blue-600 focus:ring-blue-500 rounded bg-white dark:bg-[#12242b] border-gray-300 dark:border-gray-700"
              />Restrict publication years</label
            >
            <div v-show="restrictYear" class="pl-6 mt-2">
              <p class="form-help">Include sources published in years from:</p>
              <div class="mt-8 mb-2 pb-2 pr-4">
                <vue-slider
                  ref="yearSelector"
                  v-model="yearSliderValue"
                  tooltip="always"
                  :min-range="5"
                  :marks="getYearMarks()"
                  :min="1960"
                  :max="currentYear"
                  @change="changeYear"
                ></vue-slider>
              </div>
            </div>
          </div>
          <div class="mb-6">
            <label
              class="flex items-center text-gray-700 dark:text-gray-300 font-bold text-sm cursor-pointer"
              ><input
                v-model="restrictQuartile"
                type="checkbox"
                class="mr-2 text-blue-600 focus:ring-blue-500 rounded bg-white dark:bg-[#12242b] border-gray-300 dark:border-gray-700"
              />Restrict journal quartile</label
            >
            <div v-show="restrictQuartile" class="pl-6 mt-2">
              <p class="form-help">
                Include sources published in journals with a minimum quartile:
              </p>
              <div class="mt-8 mb-2 pb-2 pr-4">
                <vue-slider
                  ref="quartileSelector"
                  v-model="quartileSliderValue"
                  tooltip="always"
                  :marks="getQuartileMarks()"
                  :min="1"
                  :max="4"
                  @change="changeQuartile"
                ></vue-slider>
              </div>
            </div>
          </div>
          <div class="mb-6">
            <label
              class="flex items-center text-gray-700 dark:text-gray-300 font-bold text-sm cursor-pointer"
              ><input
                v-model="restrictCitationCount"
                type="checkbox"
                class="mr-2 text-blue-600 focus:ring-blue-500 rounded bg-white dark:bg-[#12242b] border-gray-300 dark:border-gray-700"
              />Restrict citation count</label
            >
            <div v-show="restrictCitationCount" class="pl-6 mt-2">
              <p class="form-help">
                Include sources with a minimum citation count:
              </p>
              <div class="mb-2">
                <input
                  v-model="citationCount"
                  type="number"
                  class="form-text-input"
                />
              </div>
            </div>
          </div>
          <div class="mb-6">
            <label
              class="flex items-center text-gray-700 dark:text-gray-300 font-bold text-sm cursor-pointer"
              ><input
                v-model="restrictInfluentialCitationCount"
                type="checkbox"
                class="mr-2 text-blue-600 focus:ring-blue-500 rounded bg-white dark:bg-[#12242b] border-gray-300 dark:border-gray-700"
              />Restrict influential citation count</label
            >
            <div v-show="restrictInfluentialCitationCount" class="pl-6 mt-2">
              <p class="form-help">
                Include sources with a minimum influential citation count:
              </p>
              <div class="mb-2">
                <input
                  v-model="influentialCitationCount"
                  type="number"
                  class="form-text-input"
                />
              </div>
            </div>
          </div>
          <div class="mb-6">
            <label
              class="flex items-center text-gray-700 dark:text-gray-300 font-bold text-sm cursor-pointer"
              ><input
                v-model="restrictOpenAccessOnly"
                type="checkbox"
                class="mr-2 text-blue-600 focus:ring-blue-500 rounded bg-white dark:bg-[#12242b] border-gray-300 dark:border-gray-700"
              />Restrict open access</label
            >
            <div v-show="restrictOpenAccessOnly" class="pl-6 mt-2">
              <p class="form-help">
                Include only sources from open access journals:
              </p>
              <div class="mb-2">
                <label
                  class="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                  ><input
                    v-model="openAccessOnly"
                    type="checkbox"
                    class="mr-2 text-blue-600 focus:ring-blue-500 rounded bg-white dark:bg-[#12242b] border-gray-300 dark:border-gray-700"
                  />
                  Open Access Only</label
                >
              </div>
            </div>
          </div>
        </div>
      </template>
    </right-side-modal>
  </div>
</template>

<script>
import { nextTick, defineAsyncComponent } from "vue";
import axios from "axios";
import { vOnClickOutside } from "@vueuse/components";
import * as utils from "../../utils";
import RightSideModal from "../Modals/RightSide.vue";
import "vue-slider-component/theme/default.css";
import ContextPopper from "../ContextPopper.vue";

const VueSlider = defineAsyncComponent(() => import("vue-slider-component"));

const { keysToCamel } = utils;

export default {
  name: "SearchBox",
  components: {
    RightSideModal,
    VueSlider,
    ContextPopper,
  },
  directives: {
    onClickOutside: vOnClickOutside,
  },
  props: {
    showGenericExamples: Boolean,
    placeholder: String,
    modes: Object,
    autocompleteDirection: String,
    followupQuestions: Array,
    collaborationSpaceMetadata: Object,
    filters: Object,
    model: String,
    loadingFollowupQuestions: Boolean,
  },
  data() {
    return {
      isFocused: false,
      isLoading: false,
      loadingMessage: "",
      initialInput: "",
      suggestions: [],
      selectedSuggestionIndex: -1,
      autocompleteIsLoading: false,
      areSuggestionsVisible: false,
      isFeedbackVisible: false,
      feedbackIsLoading: false,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      debounceTimer: null,
      axiosCancelToken: null,
      minLength: 3, // for autocomplete
      yearSliderValue: [
        this.filters?.minYear ? this.filters.minYear : 1960,
        this.filters?.maxYear ? this.filters.maxYear : new Date().getFullYear(),
      ],
      quartileSliderValue: this.filters?.minQuartile
        ? this.filters.minQuartile
        : 1,
      citationCount: this.filters?.minCitationCount,
      influentialCitationCount: this.filters?.minInfluentialCitationCount,
      openAccessOnly: this.filters?.openAccessOnly,
      examples: [
        "What is the effect of caffeine on honey bee foraging?",
        "What car colors are best for hot climates?",
        "How does the CRISPR-Cas9 system identify specific DNA sequences?",
      ],
      currentYear: new Date().getFullYear(),
      restrictYear: !!(this.filters?.minYear || this.filters?.maxYear),
      restrictQuartile: !!this.filters?.minQuartile,
      restrictCitationCount: !!this.filters?.minCitationCount,
      restrictInfluentialCitationCount:
        !!this.filters?.minInfluentialCitationCount,
      restrictOpenAccessOnly: !!this.filters?.openAccessOnly,
      autocompleteHeightOffset: 0,
      feedbackHeightOffset: 0,
      feedback: {
        score: null,
        message: null,
      },
      maxDocCount: 10,
      models: [
        {
          shortLabel: "GPT-5 mini",
          label: "GPT-5 mini (OpenAI)",
          value: "azure_gpt_5_mini",
        },
        {
          shortLabel: "Claude Sonnet 4.6",
          label: "Claude Sonnet 4.6 (Anthropic)",
          value: "claude_4_6_sonnet",
        },
        {
          shortLabel: "Claude 5 Fable",
          label: "Claude 5 Fable (Anthropic)",
          value: "claude_fable_5",
        },
        {
          shortLabel: "Gemini 3.5 Flash Lite",
          label: "Gemini 3.5 Flash Lite (Google)",
          value: "gemini_3_5_flash_lite",
        },
        {
          shortLabel: "Gemini 3.7 Flash",
          label: "Gemini 3.7 Flash (Google)",
          value: "gemini_3_7_flash",
        },
        {
          shortLabel: "Gemini 3.8 Flash",
          label: "Gemini 3.8 Flash (Google)",
          value: "gemini_3_8_flash",
        },
        {
          shortLabel: "Gemini 3.0 Flash",
          label: "Gemini 3.0 Flash (Google)",
          value: "gemini_3_0_flash",
        },
        {
          shortLabel: "Gemini 3.1 Pro Preview",
          label: "Gemini 3.1 Pro Preview (Google)",
          value: "gemini_3_1_pro_preview",
        },
        {
          shortLabel: "Gemini 3.5 Flash",
          label: "Gemini 3.5 Flash (Google)",
          value: "gemini_3_5_flash",
        },
        {
          shortLabel: "Llama 4 Maverick",
          label: "Llama 4 Maverick (Meta)",
          value: "llama_4_maverick",
        },
        {
          shortLabel: "Mistral Small 3.1",
          label: "Mistral Small 3.1 (Mistral)",
          value: "mistral_3_1_small",
        },
      ],
      selectedModel: this.model,
    };
  },
  computed: {
    isAnthropicSonnet() {
      return this.selectedModel === "claude_4_6_sonnet";
    },
    isAnthropicFable() {
      return this.selectedModel === "claude_fable_5";
    },
    isFableSuspended() {
      const fableModel = this.models.find((m) => m.value === "claude_fable_5");
      return !!(fableModel && fableModel.disabled);
    },
    isGoogleModel() {
      return (
        this.selectedModel && this.selectedModel.startsWith("gemini_")
      );
    },
    isAzureModel() {
      return this.selectedModel === "azure_gpt_5_mini";
    },
    isMetaModel() {
      return this.selectedModel === "llama_4_maverick";
    },
    isMistralModel() {
      return this.selectedModel === "mistral_3_1_small";
    },
    activeAxios() {
      if (this.$route.params.viewKey || !this.$store.state.person?.hashedId) {
        return this.$api.axiosPublic;
      }
      return this.$api.axios;
    },
    filterCount() {
      let count = 0;
      if (this.restrictYear) count += 1;
      if (this.restrictQuartile) count += 1;
      if (this.restrictCitationCount) count += 1;
      if (this.restrictInfluentialCitationCount) count += 1;
      if (this.restrictOpenAccessOnly) count += 1;
      return count;
    },
  },
  watch: {
    restrictYear(newValue) {
      if (newValue === false) {
        this.saveYears(null, null);
      }
    },
    restrictQuartile(newValue) {
      if (newValue === false) {
        this.saveQuartile(null);
      } else {
        this.saveQuartile(this.quartileSliderValue);
      }
    },
    restrictCitationCount(newValue) {
      if (newValue === false) {
        this.citationCount = null;
        this.$emit("save-citation-count", null);
      } else {
        this.citationCount = 1;
        this.$emit("save-citation-count", this.citationCount);
      }
    },
    restrictInfluentialCitationCount(newValue) {
      if (newValue === false) {
        this.influentialCitationCount = null;
        this.$emit("save-influential-citation-count", null);
      } else {
        this.influentialCitationCount = 1;
        this.$emit(
          "save-influential-citation-count",
          this.influentialCitationCount,
        );
      }
    },
    restrictOpenAccessOnly(newValue) {
      if (newValue === false) {
        this.openAccessOnly = null;
        this.$emit("save-open-access", null);
      } else {
        this.openAccessOnly = true;
        this.$emit("save-open-access", this.openAccessOnly);
      }
    },
    citationCount(newValue) {
      this.$emit("save-citation-count", newValue);
    },
    influentialCitationCount(newValue) {
      this.$emit("save-influential-citation-count", newValue);
    },
    openAccessOnly(newValue) {
      this.$emit("save-open-access", newValue);
    },
    suggestions() {
      this.selectedSuggestionIndex = -1;
      this.adjustSuggestionsPosition();
    },
    followupQuestions() {
      nextTick(() => {
        this.resizeTextarea();
      });
    },
    selectedModel(newValue) {
      this.$emit("update-model", newValue);
    },
  },
  mounted() {
    this.resizeTextarea();
    const self = this;
    window.addEventListener("resize", self.resizeTextarea);
  },
  unmounted() {
    const self = this;
    window.removeEventListener("resize", self.resizeTextarea);
  },
  methods: {
    handleFocusIn() {
      this.isFocused = true;
      this.$emit("search-focus");
      nextTick(() => {
        this.resizeTextarea();
      });
    },
    handleFocusOut(e) {
      if (!this.$refs.initialInputHolder?.contains(e.relatedTarget)) {
        this.isFocused = false;
        nextTick(() => {
          this.resizeTextarea();
        });
      }
    },
    resizeTextarea() {
      const textarea = this.$refs.initialInput;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
      nextTick(() => {
        const height = this.$refs.initialInputHolder?.offsetHeight || 0;
        this.$emit("change-height", height);
      });
    },
    toggleMode(mode) {
      this.$emit("toggle-mode", mode);
    },
    handleInitialInput() {
      this.resizeTextarea();
      const self = this;
      this.fetchAutocomplete(this.initialInput, function(result) {self.autocompleteIsLoading = result});  // eslint-disable-line
    },
    hideSuggestions() {
      this.areSuggestionsVisible = false;
      this.selectedSuggestionIndex = -1;
    },
    handleArrowDown(e) {
      if (this.areSuggestionsVisible && this.suggestions.length > 0) {
        e.preventDefault();
        if (this.selectedSuggestionIndex === -1) {
          // If at input and it's a dropdown, DOWN arrow enters the top of the menu
          if (this.autocompleteDirection !== "up") {
            this.selectedSuggestionIndex = 0;
          }
        } else if (this.selectedSuggestionIndex < this.suggestions.length - 1) {
          // Inside menu: DOWN arrow moves visually downwards
          this.selectedSuggestionIndex++;
        } else {
          // At bottom of menu: wrap back to input
          this.selectedSuggestionIndex = -1;
        }
      }
    },
    handleArrowUp(e) {
      if (this.areSuggestionsVisible && this.suggestions.length > 0) {
        e.preventDefault();
        if (this.selectedSuggestionIndex === -1) {
          // If at input and it's a dropup, UP arrow enters the bottom of the menu
          if (this.autocompleteDirection === "up") {
            this.selectedSuggestionIndex = this.suggestions.length - 1;
          }
        } else if (this.selectedSuggestionIndex > 0) {
          // Inside menu: UP arrow moves visually upwards
          this.selectedSuggestionIndex--;
        } else {
          // At top of menu: wrap back to input
          this.selectedSuggestionIndex = -1;
        }
      }
    },
    handleEnter(e) {
      if (this.isFeedbackVisible) {
        e.preventDefault();
        this.hideFeedback();
        return;
      }
      if (this.areSuggestionsVisible && this.selectedSuggestionIndex !== -1) {
        e.preventDefault();
        this.selectSuggestion(this.suggestions[this.selectedSuggestionIndex]);
      } else if (!e.shiftKey) {
        e.preventDefault();
        this.evaluateQuestionBreadth();
      }
    },
    adjustSuggestionsPosition() {
      nextTick(() => {
        const ref = this.$refs.suggestions;
        const holderRef = this.$refs.initialInputHolder;
        if (ref) {
          this.autocompleteHeightOffset =
            this.autocompleteDirection === "up"
              ? ref.offsetHeight * -1
              : holderRef.offsetHeight;
        }
      });
    },
    showSuggestions() {
      this.areSuggestionsVisible = true;
      this.adjustSuggestionsPosition();
    },
    hideFeedback() {
      this.isFeedbackVisible = false;
    },
    showFeedback() {
      this.hideSuggestions();
      this.isFeedbackVisible = true;
      if (this.autocompleteDirection === "up") {
        nextTick(() => {
          const ref = this.$refs.feedback;
          this.feedbackHeightOffset = ref ? ref.offsetHeight * -1 : 0;
        });
      } else {
        const ref = this.$refs.initialInputHolder;
        this.feedbackHeightOffset = ref ? ref.offsetHeight : 0;
      }
    },
    updateMaxDocCount(newMax) {
      this.maxDocCount = newMax;
    },
    updateModel(newModel) {
      this.selectedModel = newModel;
      this.$emit("update-model", newModel);
    },
    selectSuggestion(suggestion) {
      this.initialInput = suggestion;
      this.hideSuggestions();
      this.focusInput();
      nextTick(() => {
        this.resizeTextarea();
      });
    },
    focusInput() {
      if (this.$refs.initialInput) {
        this.$refs.initialInput.focus();
      }
    },
    fetchAutocomplete(search, loading) {
      this.hideFeedback();
      if (this.minLength && search.length < this.minLength) {
        this.hideSuggestions();
        return;
      }
      this.adjustSuggestionsPosition();
      loading(true);
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        if (this.axiosCancelToken) {
          this.axiosCancelToken.cancel();
        }
        this._fetchAutocomplete(search, loading);
      }, 300);
    },
    _fetchAutocomplete(search, loading) {
      this.axiosCancelToken = axios.CancelToken.source();
      const baseUrl = this.$route.params.researchCompassId
        ? `/research-compass/${this.$route.params.researchCompassId}/autocomplete/`
        : `/research-compass/autocomplete/`;

      const params = new URLSearchParams();
      params.append("query", search);
      if (this.$route.params.collaborationSpaceId) {
        params.append(
          "collaboration_space_id",
          this.$route.params.collaborationSpaceId,
        );
      }
      if (this.$route.params.viewKey) {
        params.append("view_key", this.$route.params.viewKey);
      }

      const url = `${baseUrl}?${params.toString()}`;

      this.activeAxios
        .get(url, {
          cancelToken: this.axiosCancelToken.token,
        })
        .then((res) => {
          loading(false);
          this.suggestions = keysToCamel(res.data).suggestions;
          this.selectedSuggestionIndex = -1;
          this.showSuggestions();
          this.axiosCancelToken = null;
        })
        .catch(() => {
          loading(false);
        });
    },
    getYearMarks() {
      return [1960, 1970, 1980, 1990, 2000, 2010, 2020];
    },
    changeYear(newYears) {
      this.saveYears(newYears[0], newYears[1]);
    },
    saveYears(minYear, maxYear) {
      this.$emit("save-years", minYear, maxYear);
    },
    getQuartileMarks() {
      return [1, 2, 3, 4];
    },
    changeQuartile(newValue) {
      this.saveQuartile(newValue);
    },
    saveQuartile(minQuartile) {
      this.$emit("save-quartile", minQuartile);
    },
    toggleFiltersModal() {
      this.$refs.filtersModal.toggleModal();
    },
    evaluateQuestionBreadth() {
      if (!this.initialInput) {
        this.feedback = { score: 1, message: "Please enter a search query." };
        this.showFeedback();
        return;
      }
      if (this.initialInput.length > 1028) {
        this.feedback = {
          score: 1,
          message:
            "Please enter a research question shorter than 1028 characters. Your question will be truncated if you proceed.",
        };
        this.showFeedback();
        return;
      }
      if (!this.isLoading) {
        this.isLoading = true;
        this.loadingMessage = "Evaluating question...";
        const baseUrl = this.$route.params.researchCompassId
          ? `/research-compass/${this.$route.params.researchCompassId}/evaluate-breadth/`
          : `/research-compass/evaluate-breadth/`;

        const params = new URLSearchParams();
        params.append("question", this.initialInput);
        if (this.$route.params.collaborationSpaceId) {
          params.append(
            "collaboration_space_id",
            this.$route.params.collaborationSpaceId,
          );
        }
        if (this.$route.params.viewKey) {
          params.append("view_key", this.$route.params.viewKey);
        }

        const url = `${baseUrl}?${params.toString()}`;

        this.activeAxios.get(url).then((resp) => {
          this.isLoading = false;
          const data = keysToCamel(resp.data);
          this.feedback = data;
          if (this.feedback.score < 3) {
            this.showFeedback();
          } else {
            this.createResearchCompassQuestion();
          }
        });
      }
    },
    setLoadingMessage(msg) {
      this.loadingMessage = msg;
    },
    resetLoading(success = false) {
      this.isLoading = false;
      this.loadingMessage = "";
      this.isFocused = false;
      if (success) {
        this.initialInput = "";
      }
      if (this.$refs.initialInput) {
        this.$refs.initialInput.blur();
      }
      nextTick(() => {
        this.resizeTextarea();
      });
    },
    createResearchCompassQuestion() {
      const selectedModelObj = this.models.find((m) => m.value === this.selectedModel);
      if (selectedModelObj && selectedModelObj.disabled) {
        if (this.$toast) {
          this.$toast.warning(selectedModelObj.disabledReason || "Access to this model is currently suspended.");
        }
        return;
      }
      if (!this.isLoading) {
        if (this.$posthog) {
          this.$posthog.capture("ResearchCompassQuestionSubmitted");
        }
        if (this.$gtag) {
          this.$gtag.event("search", {
            event_category: "ResearchCompass",
            event_label: "Question Submitted",
          });
        }
        this.isLoading = true;
        this.loadingMessage = "Starting search...";
        if (this.initialInput.length > 1028) {
          this.initialInput = this.initialInput.slice(0, 1028);
        }
        this.$emit("create-research-compass-question", {
          question: this.initialInput,
          filters: this.filters,
          maxDocCount: this.maxDocCount,
        });
        // Keeping input populated while loading so the overlay maintains its height.
        // It will be cleared in `resetLoading(success)`
        this.suggestions = [];
        this.isFocused = false;
        if (this.$refs.initialInput) {
          this.$refs.initialInput.blur();
        }
        this.hideFeedback();
        this.hideSuggestions();
        nextTick(() => {
          this.resizeTextarea();
        });
      }
    },
  },
};
</script>
